import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, WritableSignal, computed, inject, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';
import { AvailableDates, ModalContentData, PauseDates } from '../../../shared/modal-content/modal-content';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { SubscriptionsService } from './subscriptions.service';
import { DeliveriesService } from '../deliveries/deliveries.service';
import { ChangeType, Subscription, UpdateSubscriptionPayload } from './subscriptions.types';
import { formatDateToReadableString } from '../../../shared/utils/formatting';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../shared/notification/notification.service';
import { LocalStorageService } from '../../../shared/local-storage.service';
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../../../shared/constants/databases';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { filter, tap } from 'rxjs';
import { ResolutionService } from '../../../shared/resolution.service';
import { RouterLink } from '@angular/router';
import { OrderService } from '../../../shared/order.service';
import { EmptyMessageComponent } from '../../../shared/empty-message/empty-message.component';
import { afterNextRender } from '@angular/core';
import { PreOrderedProduct } from '../../../product/product.types';
import { PreOrderedProductComponent } from './pre-ordered-product/pre-ordered-product.component';
import { environment } from '../../../../environments/environment';
import { Session } from '../../../shared/types/session.type';
import { depositConfig } from '../../../product/product.contstants';
import { SettingsService } from '../../settings.service';
import { DateTime } from 'luxon';
import { FirebaseCrudService } from '../../../shared/firebase-crud.service';
@Component({
  selector: 'app-subscriptions',
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgbModule,
    MatDatepickerModule,
    FormsModule,
    RouterLink,
    EmptyMessageComponent,
    PreOrderedProductComponent
  ],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.scss'
})
export class SubscriptionsComponent implements OnInit, AfterViewInit {
  @ViewChild('goToPause') goToPause!: ElementRef;

  private modalService = inject(ModalContentService);
  private subscriptionService = inject(SubscriptionsService);
  private signalStoreService = inject(SignalsStoreService);
  private deliveriesService = inject(DeliveriesService);
  private notificationService = inject(NotificationService);
  private localStorageService = inject(LocalStorageService);
  private resolutionService = inject(ResolutionService);
  private orderService = inject(OrderService);
  private firebaseCrudService = inject(FirebaseCrudService);
  #settingsService = inject(SettingsService);

  isWaitingForAPIResponse = computed(() => this.#settingsService.isWaitingForAPIResponse());

  subscriptions = computed(() => this.signalStoreService.subscriptions());
  frequencies = computed(() => this.signalStoreService.frequencies());
  deliveryZoneInfo = computed(() => this.deliveriesService.deliveryZoneInfo());
  globalPauseDate = computed(() => this.subscriptionService.globalPauseDate());
  isMobile = computed(() => this.resolutionService.isMobile());

  odooOrder = computed(() => this.orderService.odooOrder());
  isLimitedUser = computed(() => this.signalStoreService.isLimitedUser());
  isTemporaryRouteChange = signal(false);
  temporaryRouteChangeMessage = signal('');

  pauseDates: PauseDates = {
    startDate: '',
    endDate: '',
  };

  shouldShowAllPauses: WritableSignal<boolean> = signal(false);

  emptyMessage = 'You do not have any subscriptions in your account. You can add subscriptions from the market.';

  //#region Pre Orders
  preorders: WritableSignal<PreOrderedProduct[]> = signal([]);
  preOrdersDisclaimer = signal(depositConfig.preorderedProductsDisclaimer);

  //#endregion

  constructor() {
  }

  ngOnInit(): void {
    this.#getPreOrders();
    this.submitInitialReqs();
  }

  ngAfterViewInit(): void {
    this.#validateTemporaryRoute();
  }

  private submitInitialReqs() {
    this.subscriptionService.get();
  }

  openModalPause() {
    this.openModal(
      ModalContentTypes.PAUSE_SUBSCRIPTION,
      ChangeType.pause,
      true
    )?.closed.subscribe((result: { dates: string[]; delete: boolean } | null) => {
      if (!result?.dates.length) return;
      this.subscriptionService.updateAll({
        dates: result.dates,
        pause: true,
      });
    });
  }

  openModalEditPause() {
    // Convert globalPauseDate to the format expected by the modal
    const existingRanges = this.globalPauseDate().map(pause => ({
      start: pause.start,
      end: pause.end
    }));

    const modalContentData: ModalContentData = this.generateModalContentData(
      ChangeType.pause,
      true,
      undefined
    );

    // Add existing ranges to modal data if pauseSubscription exists
    if (modalContentData.pauseSubscription) {
      (modalContentData.pauseSubscription as any).existingRanges = existingRanges;
    }

    this.modalService.openModal(ModalContentTypes.PAUSE_SUBSCRIPTION, modalContentData)
      ?.closed.subscribe((result: { dates: string[]; delete: boolean } | null) => {
        if (!result) return;
        if (result.dates?.length) {
          this.subscriptionService.updateAll({
            dates: result.dates,
            pause: true,
          });
        } else if (result.delete)
          this.openModalResume();
      });
  }

  openModalResume() {

    const subscriptions = this.subscriptions()

    this.modalService
      .openModal(
        ModalContentTypes.RESUME_SUBSCRIPTION,
        {
          title: 'Resume Subscription',
          textContent: 'You are about to resume your subscription. Please select the next delivery date when you’d like to receive your items.',
          resumeSubscriptions: {
            subscriptions
          }
        }
      ).closed.subscribe(result => {
        if (!result || !result.startDates || !result.startDates.length) return;
        this.subscriptionService.updateAll({ pause: false, startDates: result.startDates });
      });

  }

  openModalCancel(subscription: Subscription) {
    this.openModal(
      ModalContentTypes.CONFIRMATION,
      ChangeType.cancel,
      true,
      subscription
    )?.closed.subscribe((result: { confirm: boolean } | null) => {
      if (!result) return;
      if (result.confirm) {
        this.subscriptionService.cancel(subscription.id);
      }
    });
  }

  openModalSkip(subscription: any) {
    this.openModal(
      ModalContentTypes.SKIP_DELIVERY,
      ChangeType.skip,
      true
    )?.closed.subscribe(
      (result: { skip: boolean; goToPause: boolean } | null) => {
        if (!result) return;
        if (result.skip) {

          // For limited users this skip will skip the entire irder
          if (this.isLimitedUser() && this.odooOrder().id) {
            this.orderService.skipOrder({ orderId: this.odooOrder().id, isSkipping: true, donationAmount: null }).pipe(
              tap(() => this.subscriptionService.get())
            ).subscribe();
            return
          }

          this.updateSubscription(subscription.id, { skip: true }, false)
            .pipe(
              tap(() =>
                this.notificationService.show({
                  text: 'Your subscription has been skipped successfully',
                  type: 'success',
                })
              )
            )
            .subscribe();

        } else if (result.goToPause) {
          this.openModalPause();
        }
      }
    );
  }

  private openModal(
    actionType: ModalContentTypes,
    changeType: ChangeType,
    fullScreenOnMobile: boolean,
    subscription?: Subscription
  ): NgbModalRef | null {
    const modalContentData: ModalContentData = this.generateModalContentData(
      changeType,
      fullScreenOnMobile,
      subscription
    );
    return this.modalService.openModal(actionType, modalContentData);
  }

  generateModalContentData(
    changeType: ChangeType,
    fullScreenOnMobile: boolean,
    subscription?: Subscription
  ): ModalContentData {
    const isBeforeCutoff =
      this.deliveryZoneInfo()?.deliveryDate ===
      this.deliveryZoneInfo()?.prevDeliveryDate;


    let formattedDeliveryDate: any = this.deliveryZoneInfo()?.deliveryDate;
    if (formattedDeliveryDate) formattedDeliveryDate = formatDateToReadableString(formattedDeliveryDate).readableString

    switch (changeType) {
      case ChangeType.cancel:
        return {
          title: 'Are you sure?',
          textContent: subscription?.skipDate ? '' : isBeforeCutoff
            ? `<b>ATTENTION! </b>Your subscription cancellation for this product will take effect immediately.`
            : `<b>ATTENTION! </b>Unfortunately, it's too late to delete this product subscription for this week. If you proceed, your cancellation will take effect on your next delivery on <b>${formattedDeliveryDate}.</b>`,
          cancelButtonText: 'Keep subscription',
          confirmButtonText: 'Cancel subscription',
          fullScreenOnMobile,
        };
      case ChangeType.pause:

        const minDateInfo = this.subscriptions().map(sub => {
          const [year, month_str, day] = sub.nextDelivery.split('-');
          const month = (+month_str) - 1;
          const dateObj = new Date(+year, +month, +day);
          return {
            stringDate: sub.nextDelivery,
            timestamp: dateObj.getTime(),
            dayName: sub.deliveryDay
          };
        }).sort((a, b) => a.timestamp - b.timestamp)[0];

        const minDate = minDateInfo.stringDate;
        const dayName = minDateInfo.dayName;

        return {
          title: 'Pause Subscription',
          fullScreenOnMobile,
          pauseSubscription: {
            minAvailableDate: minDate,
            minAvailableDay: this.isTemporaryRouteChange() ? dayName : undefined
          }
        };
      case ChangeType.resume:
        return {
          title: 'Resume subscription',
          textContent: `You are about to resume your subscription and it will take effect immediately. Delivery scheduled for ${formatDateToReadableString(
            this.deliveryZoneInfo()?.deliveryDate || ''
          ).readableString
            }`,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Resume subscription',
        };
      case ChangeType.skip:
        return {
          title: 'Wait a moment!',
          fullScreenOnMobile,
        };
      case ChangeType.update:
        return {
          title: 'Hold Up!',
          textContent:
            'You are making changes to your subscription quantity. These changes will take effect immediately.',
        };
      default:
        return {};
    }
  }

  private handleSelectionChange(
    subscription: Subscription,
    data: MatSelectChange,
    isDeliveryChange?: boolean
  ) {

    // No available to change the frequency because the route in your zone has a temporary change.
    if (this.isTemporaryRouteChange() && !isDeliveryChange)
      return;

    // Open modal for available date selection:
    const availableDates: AvailableDates[] = subscription.dateAvailables?.map(e => {
      return {
        date: formatDateToReadableString(e).readableString,
        originalDate: e
      } as AvailableDates
    }) || []

    this.modalService.openModal(ModalContentTypes.SUBSCRIPTION_NEXT_DELIVERY, {
      title: 'When would you like your next delivery?',
      textContent: `${isDeliveryChange ? 'Please' : 'In order to update your frequency, please'} select the next date that you would like to receive <b>${subscription.product.name}</b>.`,
      subscriptionNextDelivery: {
        availableDates
      },
      closeable: false,
    }, { backdrop: 'static' }).closed.subscribe(
      (response: { confirm: boolean, date: string }) => {
        if (response?.confirm) {
          if (response.date) {
            let payload: any = {
              startDate: response.date,
              frequency: isDeliveryChange ? subscription.frequency : data.value
            };

            this.updateSubscription(subscription.id, payload, false)
              .pipe(
                tap(() =>
                  this.notificationService.show({
                    text: isDeliveryChange ? `The next delivery date for the product '${subscription.product.name}' has been updated successfully.` : 'Your subscription frequency has been successfully updated',
                    type: 'success',
                  })
                ),
                tap(() => this.orderService.getOrder(false))
              ).subscribe();
          } else {
            this.notificationService.show({ text: 'You should select a valid date', type: 'warning' })
            this.subscriptionService.restorePreviousValues(subscription);
          }
        } else {
          this.subscriptionService.restorePreviousValues(subscription);
        }
      }
    )
  }

  updateSubscription(
    subscriptionId: number,
    data: Partial<UpdateSubscriptionPayload>,
    showDefaultMessage: boolean = true
  ) {
    return this.subscriptionService.update(
      subscriptionId,
      data,
      showDefaultMessage
    );
  }

  private handleQuantityChange(subscription: any, event: any) {
    const quantity = +event.target.value;
    this.updateSubscription(subscription.id, { quantity }, false)
      .pipe(
        filter(response => {
          if (response.error)
            this.subscriptionService.restorePreviousValues(subscription);

          return !response.error;
        }),
        tap(() =>
          this.notificationService.show({
            text: 'Your subscription quantity has been successfully updated',
            type: 'success',
          })
        )
      )
      .subscribe();
  }

  validateSubscriptionUpdate(params: {
    subscription: Subscription;
    event: any;
    isQuantity: boolean;
  }) {
    const avoidHoldUpMessage = this.signalStoreService.userPreferences().holdUpSubscriptions.value;

    if (avoidHoldUpMessage)
      return this.triggerUpdate(params);

    this.openModal(
      ModalContentTypes.HOLD_UP_UPDATE_SUBSCRIPTION,
      ChangeType.update,
      false
    )?.closed.subscribe(
      (result: { avoid: boolean; confirmChanges: boolean }) => {

        if (result?.avoid)
          this.#updateFirebasePreferences();

        if (result?.confirmChanges)
          this.triggerUpdate(params);
        else
          this.subscriptionService.restorePreviousValues(params.subscription);
      }
    );
  }

  triggerUpdate(params: {
    subscription: Subscription;
    event: any;
    isQuantity: boolean;
    isDeliveryChange?: boolean;
  }) {

    if (!!params.subscription?.pauseDate?.[0]?.startDate && params.subscription.isPauseActive && params.isDeliveryChange) return

    params.isQuantity
      ? this.handleQuantityChange(params.subscription, params.event)
      : this.handleSelectionChange(params.subscription, params.event, params.isDeliveryChange);
  }

  undoSkipLine(sub: Subscription) {
    this.modalService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Change of Plans?',
      textContent: `You’re about to reinstate your delivery for ${sub.skipDate}. Would you like to receive your order as originally planned?`,
      cancelButtonText: 'Keep Skipped',
      confirmButtonText: 'Unskip Week',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return;
        this.updateSubscription(sub.id, { unskip: true }, true).pipe(tap(() => this.orderService.getOrder(false))).subscribe();
      })
  }

  //#region Pre Orders
  #getPreOrders() {
    this.orderService.getPreOrders().pipe(
      tap((preOrders) => this.preorders.set(preOrders))
    ).subscribe();
  }

  afterPreOrderLineUpdated() {
    this.#getPreOrders();
  }
  //#endregion

  #validateTemporaryRoute() {
    const currentSessionInfo: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);

    if (!currentSessionInfo)
      return;

    const { temporaryChange } = currentSessionInfo?.deliveryInfo ?? {};
    this.isTemporaryRouteChange.set(!!temporaryChange?.active);

    if (this.isTemporaryRouteChange())
      this.temporaryRouteChangeMessage.set(temporaryChange?.message ?? '');
  }

  async #updateFirebasePreferences() {

    const userId = this.signalStoreService.sessionSignal()?.accountInfo.id;
    if (!userId) return;

    const preferences = this.signalStoreService.userPreferences();

    preferences.holdUpSubscriptions = {
      value: true,
      date: DateTime.utc().toMillis()
    };

    const exists = await this.firebaseCrudService.existsDoc(FIREBASE_COLLECTIONS.PREFERENCES, userId.toString());

    if (exists)
      this.firebaseCrudService.update(FIREBASE_COLLECTIONS.PREFERENCES, userId, preferences);
    else
      this.firebaseCrudService.add(FIREBASE_COLLECTIONS.PREFERENCES, userId, preferences);
  }

  changeQuantity(sub: Subscription) {

    if (!sub.quantity || +sub.quantity <= 0) {
      return this.notificationService.show({
        text: 'Quantity must be greater than zero.',
        type: 'warning'
      });
    }

    if (sub.previous) {
      if (+sub.quantity === sub.previous.quantity) {
        return this.notificationService.show({
          text: 'No changes have been detected in the quantity.',
          type: 'warning'
        })
      }
    }

    this.validateSubscriptionUpdate({
      subscription: sub,
      event: { target: { value: sub.quantity } },
      isQuantity: true
    });
  }

  openModalChangeQuantity(sub: Subscription) {

    this.modalService
      .openModal(
        ModalContentTypes.CHANGE_QUANTITY_SUBSCRIPTION,
        {
          title: 'Update Quantity',
          textContent: `Adjust the quantity of your <strong><em>${sub.product.name}</em></strong> subscription as needed`,
          quantity: sub.quantity
        }
      )
      .closed
      .subscribe(({ next, quantity }) => {
        if (!next) return;
        sub.quantity = quantity;
        this.changeQuantity(sub);
      });
  }

  toggleAllPauses() {
    this.shouldShowAllPauses.update(current => !current);
  }
}
